import React, { useEffect } from 'react';
import { Helmet } from 'react-helmet-async';
import { useTranslation } from 'react-i18next';
import { Container, Box, Typography, styled, Grid, Card, CardContent, Button } from '@mui/material';
import { Link as RouterLink } from 'react-router-dom';
import Footer from '../componentsPeopletech/Footer/Footer';
import blogVideos from '../data/blogVideos.json';

const BlogPeople = () => {
  const { t, i18n } = useTranslation();
  const currentLang = i18n.language || 'en';
  const baseUrl = 'https://www.peoplefortech.com';

  // Cargar el script de TikTok cuando el componente se monte
  useEffect(() => {
    const isPrerender = typeof navigator !== 'undefined' && /ReactSnap/i.test(navigator.userAgent);
    if (isPrerender) {
      return undefined;
    }

    // Verificar si el script ya existe
    let script = document.getElementById('tiktok-embed-script');
    
    if (!script) {
      script = document.createElement('script');
      script.src = 'https://www.tiktok.com/embed.js';
      script.async = true;
      script.id = 'tiktok-embed-script';
      document.body.appendChild(script);
    }

    // Función para renderizar los embeds después de que el script se cargue
    const renderEmbeds = () => {
      if (window.tiktokEmbed && window.tiktokEmbed.lib) {
        window.tiktokEmbed.lib.render();
      }
    };

    // Si el script ya estaba cargado, renderizar inmediatamente
    if (window.tiktokEmbed && window.tiktokEmbed.lib) {
      renderEmbeds();
    } else if (script) {
      script.onload = renderEmbeds;
    }

    // Re-renderizar después de un pequeño delay para asegurar que el DOM esté listo
    const timeoutId = setTimeout(() => {
      renderEmbeds();
    }, 1000);

    return () => {
      clearTimeout(timeoutId);
    };
  }, []);

  const videos = blogVideos;

  const getEmbedHtml = (video) =>
    `<blockquote class="tiktok-embed" cite="${video.tiktokUrl}" data-video-id="${video.id}" style="max-width: 605px;min-width: 325px;">
      <section>
        <a target="_blank" title="@peoplefortech" href="https://www.tiktok.com/@peoplefortech?refer=embed">@peoplefortech</a>
        <a target="_blank" href="${video.tiktokUrl}">${video.title}</a>
      </section>
    </blockquote>`;

  const BlogContainer = styled(Container)(({ theme }) => ({
    paddingTop: theme.spacing(8),
    paddingBottom: theme.spacing(8),
    backgroundColor: '#ffffff',
  }));

  const Title = styled(Typography)(({ theme }) => ({
    fontWeight: 700,
    fontSize: '2.5rem',
    marginBottom: theme.spacing(2),
    textAlign: 'center',
    color: '#000000',
    [theme.breakpoints.down('sm')]: {
      fontSize: '2rem',
    },
  }));

  const VideoCard = styled(Card)(({ theme }) => ({
    display: 'flex',
    flexDirection: 'column',
    height: '100%',
    boxShadow: '0 4px 6px rgba(0, 0, 0, 0.1)',
    borderRadius: theme.spacing(2),
    overflow: 'hidden',
    transition: 'transform 0.2s, box-shadow 0.2s',
    '&:hover': {
      transform: 'translateY(-4px)',
      boxShadow: '0 8px 12px rgba(0, 0, 0, 0.15)',
    },
  }));

  const VideoContainer = styled(Box)(({ theme }) => ({
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    minHeight: '600px',
    padding: theme.spacing(2),
    '& blockquote': {
      maxWidth: '605px',
      minWidth: '325px',
    },
  }));

  const DescriptionText = styled(Typography)(({ theme }) => ({
    padding: theme.spacing(2),
    color: '#414141',
    fontSize: '1rem',
    lineHeight: 1.6,
    textAlign: 'left',
  }));

  return (
    <>
      <Helmet>
        {/* Meta tags básicos */}
        <title>{t('seo.blogTitle') || 'Blog - PeopleForTech'}</title>
        <meta name="description" content={t('seo.blogDescription') || 'Videos and content from PeopleForTech'} />
        <link rel="canonical" href={`${baseUrl}/blog`} />
        <html lang={currentLang} />
        
        {/* Open Graph */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={`${baseUrl}/blog`} />
        <meta property="og:title" content={t('seo.blogTitle') || 'Blog - PeopleForTech'} />
        <meta property="og:description" content={t('seo.blogDescription') || 'Videos and content from PeopleForTech'} />
        
        {/* Twitter */}
        <meta property="twitter:card" content="summary_large_image" />
        <meta property="twitter:url" content={`${baseUrl}/blog`} />
        <meta property="twitter:title" content={t('seo.blogTitle') || 'Blog - PeopleForTech'} />
        <meta property="twitter:description" content={t('seo.blogDescription') || 'Videos and content from PeopleForTech'} />
        
        {/* Schema.org JSON-LD para SEO de IA */}
        <script type="application/ld+json">
          {JSON.stringify({
            "@context": "https://schema.org",
            "@type": "Blog",
            "name": "PeopleForTech Blog",
            "description": t('seo.blogDescription') || 'Videos and content from PeopleForTech',
            "url": `${baseUrl}/blog`,
            "publisher": {
              "@type": "Organization",
              "name": "PeopleForTech",
              "url": baseUrl,
              "logo": {
                "@type": "ImageObject",
                "url": `${baseUrl}/logo.png`
              }
            },
            "blogPost": videos.map(video => ({
              "@type": "BlogPosting",
              "headline": video.title,
              "url": `${baseUrl}/blog/${video.slug}`,
              "description": video.description,
              "keywords": [video.focusKeyword, ...video.longtailKeywords].join(", "),
              "mainEntityOfPage": `${baseUrl}/blog/${video.slug}`,
              "publisher": {
                "@type": "Organization",
                "name": "PeopleForTech"
              }
            })),
            "inLanguage": currentLang
          })}
        </script>
      </Helmet>
      <BlogContainer>
        <Title variant="h2">{t('nav.blog')}</Title>
        <Grid container spacing={4} justifyContent="center">
          {videos.map((video) => (
            <Grid item xs={12} md={6} key={video.id}>
              <VideoCard>
                <VideoContainer>
                  <div
                    dangerouslySetInnerHTML={{ __html: getEmbedHtml(video) }}
                  />
                </VideoContainer>
                <CardContent sx={{ padding: 0 }}>
                  <Typography variant="h3" sx={{ px: 2, pt: 2, fontSize: '1.2rem', fontWeight: 700 }}>
                    {video.title}
                  </Typography>
                  <DescriptionText variant="body1">
                    {video.description}
                  </DescriptionText>
                  <Box sx={{ px: 2, pb: 2, display: 'flex', gap: 1.5, flexWrap: 'wrap' }}>
                    <Button
                      component={RouterLink}
                      to={`/blog/${video.slug}`}
                      variant="contained"
                      sx={{ textTransform: 'none', backgroundColor: '#111' }}
                    >
                      Ver artículo SEO
                    </Button>
                    <Button
                      component="a"
                      href={video.tiktokUrl}
                      target="_blank"
                      rel="noopener noreferrer"
                      variant="outlined"
                      sx={{ textTransform: 'none', borderColor: '#111', color: '#111' }}
                    >
                      Ver TikTok
                    </Button>
                  </Box>
                </CardContent>
              </VideoCard>
            </Grid>
          ))}
        </Grid>
      </BlogContainer>
      <Footer />
    </>
  );
};

export default BlogPeople;

